
// src/screens/MitoDespachoScreen.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";

import jsPDF from "jspdf";
import "jspdf-autotable";
import "./mitodespacho.css";

const PAD2 = (n) => String(n).padStart(2, "0");

const ENV =
  typeof process !== "undefined" && process && process.env
    ? process.env
    : {};

const MITO_FINALIZADOS_ENDPOINT =
  ENV.REACT_APP_MITO_SERVICIOS_FINALIZADOS_GRAFICOS_URL ||
  "https://api.medic.com.ar/api/servicios/obtenerserviciosfinalizadosgraficos";

const MITO_PRODUCTIVIDAD_ENDPOINT =
  ENV.REACT_APP_MITO_SERVICIOS_PRODUCTIVIDAD_URL ||
  "https://api.medic.com.ar/api/servicios/obtenerserviciosproductividad";

const MITO_API_TOKEN =
  ENV.REACT_APP_MITO_API_TOKEN ||
  "";

const POR_MOVIL_MANUAL_STORAGE_KEY = "mito_por_movil_manual_v2";

function safeGetLocalStorage(key) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return "";
    return window.localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function getStoredToken() {
  return (
    safeGetLocalStorage("MITO_API_TOKEN") ||
    safeGetLocalStorage("mito_token") ||
    safeGetLocalStorage("medic_token") ||
    safeGetLocalStorage("token") ||
    ""
  );
}


function safeSetLocalStorage(key, value) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(key, value);
  } catch {
  }
}

function safeParseJSON(value, fallback) {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function getStoredPorMovilManual() {
  const parsed = safeParseJSON(safeGetLocalStorage(POR_MOVIL_MANUAL_STORAGE_KEY), {});
  return parsed && typeof parsed === "object" ? parsed : {};
}

function buildPorMovilManualKey(fechaISO, base, movil) {
  return `${String(fechaISO || "").trim()}__${normalizeCompareText(base)}__${String(movil || "").trim()}`;
}

function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${PAD2(d.getMonth() + 1)}-${PAD2(d.getDate())}`;
}

const MITO_MIN_DATE = "2026-03-01";

function clampFechaISO(value) {
  const v = String(value || "").trim();
  if (!v) return MITO_MIN_DATE;
  return v < MITO_MIN_DATE ? MITO_MIN_DATE : v;
}

function labelFecha(iso) {
  try {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("es-AR", {
      weekday: "long",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function hhmmss(date) {
  if (!date) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
}

function parseHHMM(hhmm) {
  const onlyHour = String(hhmm || "00:00").slice(0, 5);
  const [h, m] = onlyHour.split(":").map((x) => parseInt(x, 10));
  return (h || 0) * 60 + (m || 0);
}

function inRangeWrap(min, startMin, endMin) {
  if (startMin <= endMin) return min >= startMin && min < endMin;
  return min >= startMin || min < endMin;
}

function timeBucket(hhmm) {
  const t = parseHHMM(hhmm);
  if (t >= 0 && t < 6 * 60) return "00:00 a 06:00";
  if (t >= 6 * 60 && t < 12 * 60) return "06:00 a 12:00";
  if (t >= 12 * 60 && t < 18 * 60) return "12:00 a 18:00";
  return "18:00 a 24:00";
}

function ColorBadge({ color }) {
  const cls =
    color === "ROJO"
      ? "rojo"
      : color === "AMARILLO"
        ? "amarillo"
        : color === "VERDE"
          ? "verde"
          : color === "FUCSIA"
            ? "fuccia"
            : color === "CELESTE"
              ? "azul"
              : "blanco";

  return <span className={`mito-badge ${cls}`}>{color || "—"}</span>;
}

const PRESTADORES_ZONA_MEDIC = ["CMC", "INTERZONAS", "Dr. Rodriguez", "Dr. Pardal", "Dr. Rojas"];
const PRESTADORES_FUERA_ZONA = [
  "UC San Martin",
  "Vittal",
  "Max salud",
  "Cardio sur",
  "Semp",
  "Presencia medica",
  "Priormed",
  "Swiss medical",
  "Ermed",
];

const SOCIOS_TRASLADO = [
  "HOMINNIS",
  "AGUERO",
  "SWISS MEDICAL",
  "EMERGER",
  "AMTA",
  "San Juan de Dios",
  "PAMI",
  "galeno",
  "Mastered",
  "SAME",
  "Vitas",
  "Áreas Protegida",
  "Cardio",
  "Solis (S.H. San Justo. S.A)",
  "Sifeme",
  "Sanatorio Mariano Acosta",
  "Interzona",
  "Doctor Red",
  "Gerdana",
  "Vittal",
  "Socio directo",
  "Uom",
  "Evento",
];

const BASE_HORARIOS_DEFAULT = [
  { match: "CASANOVA", horario: "06:00 a 06:00" },
  { match: "MORON", horario: "07:00 a 07:00" },
  { match: "MEDIANA", horario: "07:00 a 07:00" },
  { match: "SAN JUSTO", horario: "07:00 a 07:00" },
  { match: "CASEROS", horario: "07:00 a 07:00" },
  { match: "UTIM 16", horario: "08:00 a 00:00" },
  { match: "RAMOS MEJIA", horario: "10:00 a 22:00" },
  { match: "LAFERRERE", horario: "09:00 a 21:00" },
  { match: "APOYO 12", horario: "12:00 a 00:00" },
];

function pickFirst(...values) {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    return value;
  }
  return "";
}

function toUpperText(value) {
  return String(value || "").trim().toUpperCase();
}

function formatApiDatePart(date) {
  return `${PAD2(date.getMonth() + 1)}/${PAD2(date.getDate())}/${date.getFullYear()}`;
}

function buildMitoRange(fechaISO) {
  const [y, m, d] = String(fechaISO || isoToday()).split("-").map(Number);
  const from = new Date(y, (m || 1) - 1, d || 1, 6, 0, 0);
  const to = new Date(from);
  to.setDate(to.getDate() + 1);

  return {
    fdesde: `${formatApiDatePart(from)} 06:00`,
    fhasta: `${formatApiDatePart(to)} 06:00`,
  };
}

function extractDateOnly(value) {
  const str = String(value || "");
  const isoMatch = str.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];

  const slashMatch = str.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slashMatch) {
    const [, mm, dd, yyyy] = slashMatch;
    return `${yyyy}-${mm}-${dd}`;
  }

  return "";
}

function extractHour(value) {
  const str = String(value || "");
  const isoMatch = str.match(/T(\d{2}:\d{2}:\d{2})/);
  if (isoMatch) return isoMatch[1];

  const hourMatch = str.match(/(\d{2}:\d{2})(?::(\d{2}))?/);
  if (hourMatch) {
    const base = hourMatch[1];
    const sec = hourMatch[2] || "00";
    return `${base}:${sec}`;
  }

  return "";
}


function normalizeCompareText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

function extractRowsFromPayload(payload) {
  if (Array.isArray(payload)) return payload;

  const candidates = [
    payload?.responseFinalizados,
    payload?.responseServicios,
    payload?.responseProductividad,
    payload?.responseRows,
    payload?.data,
    payload?.items,
    payload?.rows,
    payload?.result,
    payload?.results,
    payload?.servicios,
    payload?.finalizados,
  ];

  for (const item of candidates) {
    if (Array.isArray(item)) return item;
  }

  return [];
}

function extractPayloadArray(payload, keys) {
  if (!payload || typeof payload !== "object") return [];

  for (const key of keys) {
    const value = payload?.[key];
    if (Array.isArray(value)) return value;
  }

  return [];
}

function formatHorarioFromDates(fechaArmado, fechaCerrado) {
  const desde = extractHour(fechaArmado);
  const hasta = extractHour(fechaCerrado);
  if (!desde && !hasta) return "";
  if (desde && hasta) return `${desde.slice(0, 5)} a ${hasta.slice(0, 5)}`;
  if (desde) return `${desde.slice(0, 5)} a --:--`;
  return `--:-- a ${hasta.slice(0, 5)}`;
}

function buildTripulacionHorarioMaps(items) {
  const byTripulacionId = new Map();
  const byMovilId = new Map();

  for (const item of items || []) {
    const tripId = String(pickFirst(item?.id, item?.tripulacionId, item?.tripulacionid) || "").trim();
    const movilId = String(pickFirst(item?.movilId, item?.movilid, item?.moviId, item?.moviid) || "").trim();
    const fechaArmado = pickFirst(item?.fechaArmado, item?.fArmado, item?.armado);
    const fechaCerrado = pickFirst(item?.fechaCerrado, item?.fCerrado, item?.cerradoFecha, item?.fechaFin);
    const horario = formatHorarioFromDates(fechaArmado, fechaCerrado);

    if (!horario) continue;

    if (tripId) {
      byTripulacionId.set(tripId, horario);
    }

    if (movilId) {
      const current = byMovilId.get(movilId);
      if (!current) {
        byMovilId.set(movilId, {
          desde: fechaArmado ? String(fechaArmado) : "",
          hasta: fechaCerrado ? String(fechaCerrado) : "",
        });
      } else {
        if (fechaArmado && (!current.desde || String(fechaArmado) < current.desde)) current.desde = String(fechaArmado);
        if (fechaCerrado && (!current.hasta || String(fechaCerrado) > current.hasta)) current.hasta = String(fechaCerrado);
      }
    }
  }

  const byMovilHorario = new Map();
  byMovilId.forEach((value, key) => {
    const horario = formatHorarioFromDates(value.desde, value.hasta);
    if (horario) byMovilHorario.set(key, horario);
  });

  return { byTripulacionId, byMovilHorario };
}

function applyTripulacionHorarios(rows, tripulaciones) {
  const { byTripulacionId, byMovilHorario } = buildTripulacionHorarioMaps(tripulaciones);

  return (rows || []).map((row) => {
    const tripId = String(row.tripulacionId || "").trim();
    const movilId = String(row.movil || "").trim();
    const horarioOperativo =
      byTripulacionId.get(tripId) ||
      byMovilHorario.get(movilId) ||
      row.horarioOperativo ||
      "";

    return normalizeRow({
      ...row,
      horarioOperativo,
    });
  });
}

function normalizeDerivacionEdad(rawValue, edadNumerica) {
  const txt = normalizeCompareText(rawValue);
  if (txt.includes("PED")) return "Pediátricos";
  if (txt.includes("ADUL")) return "Adultos";

  const edad = Number(edadNumerica);
  if (!Number.isNaN(edad) && edad > 0) {
    return edad < 15 ? "Pediátricos" : "Adultos";
  }

  return "";
}

function normalizeTrasladoTipo(rawValue) {
  const txt = normalizeCompareText(rawValue);
  if (!txt) return "";
  if (txt.includes("UTIM")) return "UTIM";
  if (txt.includes("UTIP")) return "UTIP";
  if (txt.includes("CON") && txt.includes("MED")) return "CON_MEDICO";
  if (txt.includes("SIN") && txt.includes("MED")) return "SIN_MEDICO";
  return txt;
}

function normalizeTipoServicio(rawValue) {
  const txt = normalizeCompareText(rawValue);
  if (!txt) return "";
  if (txt.includes("TRAS")) return "TRASLADO";
  if (txt.includes("DERIV")) return "DERIVACION";
  if (txt.includes("EVENT")) return "EVENTO";
  if (txt.includes("ANUL")) return "ANULADO";
  return txt;
}

function resolveOptionLabel(value, options) {
  const normalized = normalizeCompareText(value);
  if (!normalized) return "";
  const found = options.find((option) => normalizeCompareText(option) === normalized);
  return found || String(value || "").trim();
}

function guessZonaMedic(prestador) {
  const normalized = normalizeCompareText(prestador);
  if (!normalized) return false;
  if (PRESTADORES_ZONA_MEDIC.some((option) => normalizeCompareText(option) === normalized)) return true;
  if (PRESTADORES_FUERA_ZONA.some((option) => normalizeCompareText(option) === normalized)) return false;
  return false;
}

function guessHorarioOperativo(base, currentHorario) {
  if (String(currentHorario || "").trim()) return currentHorario;
  const normalizedBase = normalizeCompareText(base);
  const found = BASE_HORARIOS_DEFAULT.find((item) => normalizedBase.includes(item.match));
  return found?.horario || "";
}

function mergeRowData(baseRow, extraRow) {
  const merged = { ...baseRow };
  const keys = new Set([...Object.keys(baseRow || {}), ...Object.keys(extraRow || {})]);

  keys.forEach((key) => {
    const extraValue = extraRow?.[key];

    if (typeof extraValue === "boolean") {
      merged[key] = extraValue;
      return;
    }

    if (typeof extraValue === "number") {
      if (!Number.isNaN(extraValue)) merged[key] = extraValue;
      return;
    }

    if (extraValue !== undefined && extraValue !== null && String(extraValue).trim() !== "") {
      merged[key] = extraValue;
    }
  });

  return normalizeRow(merged);
}

function mergeRowsById(baseRows, extraRows) {
  const map = new Map();

  for (const row of baseRows || []) {
    map.set(String(row.id || `${row.base || ""}-${row.movil || ""}-${row.hora || ""}`), normalizeRow(row));
  }

  for (const row of extraRows || []) {
    const key = String(row.id || `${row.base || ""}-${row.movil || ""}-${row.hora || ""}`);
    if (!map.has(key)) {
      map.set(key, normalizeRow(row));
    } else {
      map.set(key, mergeRowData(map.get(key), row));
    }
  }

  const out = Array.from(map.values());
  out.sort((a, b) => {
    const aKey = `${a.fechaISO || ""} ${a.hora || ""} ${a.id || ""}`;
    const bKey = `${b.fechaISO || ""} ${b.hora || ""} ${b.id || ""}`;
    return aKey.localeCompare(bKey);
  });
  return out;
}

function normalizeRow(r) {
  return {
    id: r?.id ?? `${r?.fechaISO ?? "x"}-${Math.random().toString(16).slice(2)}`,
    fechaISO: r?.fechaISO ?? "",
    hora: r?.hora ?? "",
    horaFin: r?.horaFin ?? "",
    base: r?.base ?? "",
    baseId: r?.baseId ?? "",
    movil: r?.movil ?? "",
    tripulacionId: r?.tripulacionId ?? "",
    color: r?.color ?? "",
    estado: r?.estado ?? "",
    tipo: r?.tipo ?? "",
    convenio: r?.convenio ?? "",
    localidad: r?.localidad ?? "",
    operador: r?.operador ?? "",
    incidente: r?.incidente ?? "",
    demoraMin: r?.demoraMin ?? "",
    horarioAnulacion: r?.horarioAnulacion ?? "",
    observaciones: r?.observaciones ?? "",
    horarioOperativo: r?.horarioOperativo ?? "",
    prestador: r?.prestador ?? "",
    zonaMedic: typeof r?.zonaMedic === "boolean" ? r.zonaMedic : undefined,
    socioTraslado: r?.socioTraslado ?? "",
    trasladoTipo: r?.trasladoTipo ?? "",
    derivacionEdad: r?.derivacionEdad ?? "",
    servicioDesde: r?.servicioDesde ?? "",
    servicioHasta: r?.servicioHasta ?? "",
  };
}


function mapAnuladoApiRow(item) {
  const row = mapServicioApiRow(item);
  return normalizeRow({
    ...row,
    estado: "ANULADO",
    tipo: row.tipo === "ANULADO" ? row.tipo : row.tipo,
    horarioAnulacion: extractHour(
      pickFirst(item?.fanulado, item?.fAnulado, item?.horarioAnulacion, item?.fechaAnulacion, item?.fechaCerrado),
    ) || row.horarioAnulacion || row.hora,
    observaciones: pickFirst(item?.observaciones, item?.obs, item?.comentarios, item?.motivoAnulacion) || row.observaciones,
    demoraMin: pickFirst(item?.demoraMin, item?.demora, item?.minutosDemora) || row.demoraMin,
  });
}

function mapServicioApiRow(item) {
  const tripulacion = pickFirst(item?.tripulaciones, item?.tripulacion, item?.movilData) || {};
  const hasTripulacion = Boolean(tripulacion && Object.keys(tripulacion).length);
  const baseObj = pickFirst(tripulacion?.bases, tripulacion?.base, item?.bases, item?.base) || {};
  const movilObj = pickFirst(item?.moviles, item?.movilData, item?.movil) || {};
  const prestadorObj = pickFirst(item?.prestadores, item?.prestadorData) || {};

  const tomado = pickFirst(item?.ftomado, item?.fTomado, item?.fechaTomado, item?.fdesdeServicio);
  const fin = pickFirst(item?.ffin, item?.fFin, item?.fechaFin, item?.fhastaServicio);
  const anulacion = pickFirst(item?.fanulado, item?.fAnulado, item?.horarioAnulacion, item?.fechaAnulacion);

  const baseNombre = pickFirst(baseObj?.nombre, baseObj?.descripcion, item?.baseNombre, item?.base);
  const prestadorDesdeMovil = pickFirst(
    movilObj?.nombre,
    movilObj?.descripcion,
    item?.movilNombre,
    item?.movilesNombre,
  );

  const prestadorNombre = resolveOptionLabel(
    pickFirst(
      prestadorObj?.nombre,
      item?.prestador,
      item?.prestadorNombre,
      item?.prestador_nombre,
      item?.nombrePrestador,
      !hasTripulacion ? prestadorDesdeMovil : "",
    ),
    [...PRESTADORES_ZONA_MEDIC, ...PRESTADORES_FUERA_ZONA, ...SOCIOS_TRASLADO],
  );

  const socioTraslado = resolveOptionLabel(
    pickFirst(
      item?.socioTraslado,
      item?.socio,
      item?.socioNombre,
      item?.socio_nombre,
      item?.convenio,
      item?.cobertura,
      !hasTripulacion ? prestadorDesdeMovil : "",
    ),
    SOCIOS_TRASLADO,
  );

  const edadRaw = pickFirst(item?.edad, item?.edadPaciente, item?.edad_paciente, item?.anios, item?.años);

  const horarioOperativo = guessHorarioOperativo(
    baseNombre,
    pickFirst(
      tripulacion?.horarioOperativo,
      tripulacion?.horario,
      item?.horarioOperativo,
      item?.horario,
    ),
  );

  const tipoServicioNormalizado = normalizeTipoServicio(
    pickFirst(
      item?.tipo,
      item?.tiposervicio,
      item?.servicioTipo,
      item?.tipoServicio,
      item?.tipo_servicio,
    ),
  );

  const esTrasladoDePrestador = !hasTripulacion && Boolean(prestadorDesdeMovil);

  return normalizeRow({
    id: pickFirst(item?.id, item?.servicioid, item?.servicioId),
    fechaISO: extractDateOnly(tomado || fin || anulacion),
    hora: extractHour(tomado),
    horaFin: extractHour(fin),
    base: baseNombre,
    baseId: pickFirst(baseObj?.id, item?.baseid, item?.baseId),
    movil: String(
      pickFirst(
        tripulacion?.moviId,
        tripulacion?.moviid,
        tripulacion?.movilid,
        tripulacion?.movilId,
        movilObj?.moviId,
        movilObj?.movilId,
        item?.moviId,
        item?.moviid,
        item?.movilid,
        item?.movilId,
        item?.movil,
      ) || ""
    ),
    tripulacionId: pickFirst(tripulacion?.id, item?.tripulacionid, item?.tripulacionId),
    color: toUpperText(pickFirst(item?.color, item?.codificacion, item?.prioridad)),
    estado: toUpperText(pickFirst(item?.estado, "FINALIZADO")),
    tipo: tipoServicioNormalizado || (esTrasladoDePrestador ? "TRASLADO" : ""),
    convenio: pickFirst(item?.convenio, item?.cobertura, item?.financiador, item?.obraSocial),
    localidad: pickFirst(item?.localidad, item?.destino, item?.lugar),
    operador: pickFirst(item?.operador, item?.usuario, item?.despachador),
    incidente: pickFirst(item?.incidente, item?.motivo, item?.detalle, item?.descripcion),
    demoraMin: pickFirst(item?.demoraMin, item?.demora, item?.minutosDemora),
    horarioAnulacion: extractHour(anulacion),
    observaciones: pickFirst(item?.observaciones, item?.obs, item?.comentarios),
    horarioOperativo,
    prestador: prestadorNombre,
    zonaMedic: pickFirst(item?.zonaMedic, item?.enZonaMedic, item?.zona_medic, undefined),
    socioTraslado,
    trasladoTipo: normalizeTrasladoTipo(
      pickFirst(
        item?.trasladoTipo,
        item?.tipoTraslado,
        item?.tipo_traslado,
        movilObj?.tipo,
        movilObj?.descripcion,
      ),
    ),
    derivacionEdad: normalizeDerivacionEdad(
      pickFirst(item?.derivacionEdad, item?.edadDerivacion),
      edadRaw,
    ),
    servicioDesde: String(tomado || ""),
    servicioHasta: String(fin || ""),
  });
}

export default function MitoDespachoScreen() {
  const [fechaISO, setFechaISO] = useState(clampFechaISO(isoToday()));
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [rows, setRows] = useState([]);
  const [productividadRows, setProductividadRows] = useState([]);
  const [anuladosRows, setAnuladosRows] = useState([]);
  const [tripulacionesRows, setTripulacionesRows] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [lastRange, setLastRange] = useState(buildMitoRange(clampFechaISO(isoToday())));
  const [porMovilManual, setPorMovilManual] = useState(() => getStoredPorMovilManual());

  const [openProd, setOpenProd] = useState(false);
  const [openAnu, setOpenAnu] = useState(false);

  const inflightRef = useRef(false);

  const fetchData = async () => {
    if (inflightRef.current) return;

    inflightRef.current = true;
    setLoading(true);
    setErrorMsg("");

    try {
      const safeFechaISO = clampFechaISO(fechaISO);
      if (safeFechaISO !== fechaISO) {
        setFechaISO(safeFechaISO);
      }
      const range = buildMitoRange(safeFechaISO);
      setLastRange(range);

      const token = MITO_API_TOKEN || getStoredToken();
      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const requestConfig = {
        method: "POST",
        headers,
        body: JSON.stringify(range),
      };

      const [finalizadosResult, productividadResult] = await Promise.allSettled([
        fetch(MITO_FINALIZADOS_ENDPOINT, requestConfig),
        fetch(MITO_PRODUCTIVIDAD_ENDPOINT, requestConfig),
      ]);

      let finalizadosRows = [];
      let productividadRowsApi = [];
      let anuladosRowsApi = [];
      let tripulacionesRowsApi = [];

      if (finalizadosResult.status === "fulfilled") {
        if (!finalizadosResult.value.ok) {
          throw new Error(`Error ${finalizadosResult.value.status} al consultar MITO finalizados`);
        }
        const payload = await finalizadosResult.value.json();
        finalizadosRows = extractRowsFromPayload(payload).map(mapServicioApiRow);
      }

      if (productividadResult.status === "fulfilled") {
        if (!productividadResult.value.ok) {
          console.warn(`MITO productividad devolvió ${productividadResult.value.status}`);
        } else {
          const payload = await productividadResult.value.json();
          productividadRowsApi = extractPayloadArray(payload, ["responseFinalizados", "responseServicios", "responseProductividad"]).map(mapServicioApiRow);
          anuladosRowsApi = extractPayloadArray(payload, ["responseAnulados", "anulados"]).map(mapAnuladoApiRow);
          tripulacionesRowsApi = extractPayloadArray(payload, ["responseTripulaciones", "tripulaciones"]);
        }
      } else if (productividadResult.reason) {
        console.warn("MITO productividad no disponible:", productividadResult.reason);
      }

      if (!finalizadosRows.length && !productividadRowsApi.length && !anuladosRowsApi.length) {
        throw new Error("MITO no devolvió registros para el rango solicitado");
      }

      const mergedBase = mergeRowsById(finalizadosRows, productividadRowsApi);
      const mergedWithAnulados = mergeRowsById(mergedBase, anuladosRowsApi);
      const rowsWithHorarios = applyTripulacionHorarios(mergedWithAnulados, tripulacionesRowsApi);
      const productividadWithHorarios = applyTripulacionHorarios(
        mergeRowsById(productividadRowsApi.length ? productividadRowsApi : mergedBase, finalizadosRows),
        tripulacionesRowsApi,
      );
      const anuladosWithHorarios = applyTripulacionHorarios(anuladosRowsApi, tripulacionesRowsApi);

      setRows(rowsWithHorarios);
      setProductividadRows(productividadWithHorarios.length ? productividadWithHorarios : rowsWithHorarios);
      setAnuladosRows(anuladosWithHorarios);
      setTripulacionesRows(tripulacionesRowsApi);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("MITO fetch error:", error);
      setRows([]);
      setProductividadRows([]);
      setAnuladosRows([]);
      setTripulacionesRows([]);
      setErrorMsg(error instanceof Error ? error.message : "No se pudo consultar MITO");
    } finally {
      setLoading(false);
      inflightRef.current = false;
    }
  };

  useEffect(() => {
    fetchData();
  }, [fechaISO]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => fetchData(), 8000);
    return () => clearInterval(id);
  }, [autoRefresh, fechaISO]);

  useEffect(() => {
    safeSetLocalStorage(POR_MOVIL_MANUAL_STORAGE_KEY, JSON.stringify(porMovilManual));
  }, [porMovilManual]);

  const updatePorMovilManual = (row, field, value) => {
    const rowKey = buildPorMovilManualKey(fechaISO, row.base, row.movil);
    setPorMovilManual((current) => ({
      ...current,
      [rowKey]: {
        ...(current[rowKey] || {}),
        [field]: value,
      },
    }));
  };

  const analysisRows = useMemo(() => {
    return productividadRows.length ? productividadRows : rows;
  }, [productividadRows, rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      return (
        String(r.id || "").toLowerCase().includes(q) ||
        String(r.hora || "").toLowerCase().includes(q) ||
        String(r.horaFin || "").toLowerCase().includes(q) ||
        String(r.base || "").toLowerCase().includes(q) ||
        String(r.movil || "").toLowerCase().includes(q) ||
        String(r.estado || "").toLowerCase().includes(q) ||
        String(r.tipo || "").toLowerCase().includes(q) ||
        String(r.convenio || "").toLowerCase().includes(q) ||
        String(r.localidad || "").toLowerCase().includes(q) ||
        String(r.incidente || "").toLowerCase().includes(q)
      );
    });
  }, [rows, query]);

  const anulados = useMemo(() => {
    if (anuladosRows.length) return anuladosRows;
    return rows.filter((r) => r.estado === "ANULADO");
  }, [anuladosRows, rows]);

  const anuladosSheetRows = useMemo(() => {
    return anulados.map((r, idx) => ({
      key: r.id || String(idx),
      convenio: r.convenio || "",
      total: 1,
      rojo: r.color === "ROJO" ? 1 : 0,
      amarillo: r.color === "AMARILLO" ? 1 : 0,
      verde: r.color === "VERDE" ? 1 : 0,
      demora: r.demoraMin ?? "",
      horarioAnulacion: r.horarioAnulacion || r.hora || "",
      incidente: r.incidente || "",
      observaciones: r.observaciones || "",
      localidad: r.localidad || "",
    }));
  }, [anulados]);

  const anuladosTotals = useMemo(() => {
    const t = { total: 0, rojo: 0, amarillo: 0, verde: 0 };
    for (const r of anuladosSheetRows) {
      t.total += r.total || 0;
      t.rojo += r.rojo || 0;
      t.amarillo += r.amarillo || 0;
      t.verde += r.verde || 0;
    }
    return t;
  }, [anuladosSheetRows]);

  const anuladosPorHora = useMemo(() => {
    const buckets = ["00:00 a 06:00", "06:00 a 12:00", "12:00 a 18:00", "18:00 a 24:00"];
    const base = {
      ROJO: Object.fromEntries(buckets.map((b) => [b, 0])),
      AMARILLO: Object.fromEntries(buckets.map((b) => [b, 0])),
      VERDE: Object.fromEntries(buckets.map((b) => [b, 0])),
    };

    for (const a of anulados) {
      const b = timeBucket(a.horarioAnulacion || a.hora);
      if (a.color === "ROJO") base.ROJO[b] += 1;
      if (a.color === "AMARILLO") base.AMARILLO[b] += 1;
      if (a.color === "VERDE") base.VERDE[b] += 1;
    }

    const row = (colorKey, label) => {
      const c = base[colorKey];
      const total = buckets.reduce((sum, k) => sum + (c[k] || 0), 0);
      return { label, ...c, Total: total };
    };

    const totalsRow = {
      label: "Totales:",
      ...Object.fromEntries(buckets.map((b) => [b, base.ROJO[b] + base.AMARILLO[b] + base.VERDE[b]])),
      Total: buckets.reduce((sum, b) => sum + (base.ROJO[b] + base.AMARILLO[b] + base.VERDE[b]), 0),
    };

    return {
      buckets,
      rows: [row("ROJO", "Rojo"), row("AMARILLO", "Amarillo"), row("VERDE", "Verde"), totalsRow],
    };
  }, [anulados]);

  const periodDesde = useMemo(() => `${fechaISO} 06:00`, [fechaISO]);
  const periodHasta = useMemo(() => {
    try {
      const [y, m, d] = fechaISO.split("-").map(Number);
      const dt = new Date(y, m - 1, d);
      dt.setDate(dt.getDate() + 1);
      return `${dt.getFullYear()}-${PAD2(dt.getMonth() + 1)}-${PAD2(dt.getDate())} 06:00`;
    } catch {
      return `${fechaISO} 06:00`;
    }
  }, [fechaISO]);

  const isRealizado = (r) => r.estado === "FINALIZADO" || r.estado === "DESPACHADO";

  const productividadResumen = useMemo(() => {
    const totColor = { ROJO: 0, AMARILLO: 0, VERDE: 0, FUCSIA: 0, CELESTE: 0 };
    let traslados = 0;
    let eventos = 0;

    const derPorEdad = { Adultos: 0, Pediátricos: 0 };
    const derPorColor = { ROJO: 0, AMARILLO: 0, VERDE: 0, FUCSIA: 0 };

    const servicios = analysisRows.filter((r) => r.estado !== "ANULADO");
    const serviciosNoNegros = servicios.filter((r) => normalizeCompareText(r.color) !== "NEGRO");
    const totalServicios = serviciosNoNegros.length;

    for (const r of serviciosNoNegros) {
      const isTraslado = r.tipo === "TRASLADO" || Boolean(r.socioTraslado && r.trasladoTipo);
      const isDerivacion = r.tipo === "DERIVACION" || Boolean(r.prestador && r.derivacionEdad);

      if (totColor[r.color] !== undefined) totColor[r.color] += 1;
      if (isTraslado) traslados += 1;
      if (r.tipo === "EVENTO") eventos += 1;

      if (isDerivacion) {
        if (derPorEdad[r.derivacionEdad] !== undefined) derPorEdad[r.derivacionEdad] += 1;
        if (derPorColor[r.color] !== undefined) derPorColor[r.color] += 1;
      }
    }

    const pamiBaseRows = serviciosNoNegros.filter((r) => {
      const baseText = normalizeCompareText(r.base);
      return baseText === "PAMI" || baseText.includes("PAMI");
    });
    const pamiColor = { ROJO: 0, AMARILLO: 0, VERDE: 0, CELESTE: 0 };

    for (const r of pamiBaseRows) {
      if (pamiColor[r.color] !== undefined) pamiColor[r.color] += 1;
    }

    return { totColor, traslados, eventos, derPorEdad, derPorColor, totalServicios, pamiColor };
  }, [analysisRows]);

const porMovilRows = useMemo(() => {
    const map = new Map();

    for (const r of analysisRows) {
      const key = `${r.base || ""}__${r.movil || ""}`;
      if (!map.has(key)) {
        map.set(key, {
          base: r.base,
          movil: r.movil,
          horario: guessHorarioOperativo(r.base, r.horarioOperativo || ""),
          operativo: "",
          horasOperativas: "",
          realizados8a20: 0,
          realizados20a08: 0,
          totalServicios: 0,
          derivacionesInoperantes: 0,
          ingresados22a6: 0,
          realizados22a6: 0,
          registrosTotales: 0,
          negros: 0,
          derivaciones: 0,
        });
      }

      const obj = map.get(key);
      obj.registrosTotales += 1;

      const t = parseHHMM(r.hora);
      const is8a20 = inRangeWrap(t, 8 * 60, 20 * 60);
      const is20a08 = !is8a20;
      const is22a6 = inRangeWrap(t, 22 * 60, 6 * 60);
      const isNegro = normalizeCompareText(r.color) === "NEGRO";
      const isDerivacion = r.tipo === "DERIVACION" || Boolean(r.prestador && r.derivacionEdad);

      if (is22a6) obj.ingresados22a6 += 1;
      if (isNegro) obj.negros += 1;
      if (isDerivacion) obj.derivaciones += 1;
      if (isNegro || isDerivacion) obj.derivacionesInoperantes += 1;

      if (!isNegro && isRealizado(r)) {
        if (is8a20) obj.realizados8a20 += 1;
        if (is20a08) obj.realizados20a08 += 1;
        if (is22a6) obj.realizados22a6 += 1;
      }
    }

    const out = Array.from(map.values());

    for (const row of out) {
      const [desde, hasta] = String(row.horario || "").split(" a ").map((s) => s.trim());
      if (desde && hasta) {
        const dMin = parseHHMM(desde);
        const hMin = parseHHMM(hasta);
        const dur = dMin === hMin ? 24 * 60 : (hMin - dMin + 24 * 60) % (24 * 60);
        row.horasOperativas = `${Math.round(dur / 60)} hs`;
      } else {
        row.horasOperativas = "";
      }

      row.totalServicios = row.realizados8a20 + row.realizados20a08;
      row.operativo = row.registrosTotales > 0 ? "SI" : "NO";
    }

    return out.sort((a, b) => {
      const aKey = `${String(a.base || "")}-${String(a.horario || "")}-${String(a.movil || "")}`;
      const bKey = `${String(b.base || "")}-${String(b.horario || "")}-${String(b.movil || "")}`;
      return aKey.localeCompare(bKey);
    });
  }, [analysisRows]);

  const porMovilDisplayRows = useMemo(() => {
    return porMovilRows.map((row) => {
      const rowKey = buildPorMovilManualKey(fechaISO, row.base, row.movil);
      const manual = porMovilManual[rowKey] || {};

      return {
        ...row,
        manualKey: rowKey,
        horarioManual: manual.horario ?? "",
        horasOperativasManual: manual.horasOperativas ?? "",
        operativoManual: manual.operativo ?? "",
      };
    });
  }, [fechaISO, porMovilManual, porMovilRows]);

const prestadoresTabla = useMemo(() => {
    const realizados = analysisRows.filter((r) => isRealizado(r));

    const zona = PRESTADORES_ZONA_MEDIC.map((p) => ({ prestador: p, total: 0 }));
    const fuera = PRESTADORES_FUERA_ZONA.map((p) => ({ prestador: p, total: 0 }));

    const mapZona = new Map(zona.map((r) => [normalizeCompareText(r.prestador), r]));
    const mapFuera = new Map(fuera.map((r) => [normalizeCompareText(r.prestador), r]));

    for (const r of realizados) {
      if (!r.prestador) continue;
      const prestadorKey = normalizeCompareText(r.prestador);
      const zonaMedic = typeof r.zonaMedic === "boolean" ? r.zonaMedic : guessZonaMedic(r.prestador);

      if (zonaMedic && mapZona.has(prestadorKey)) mapZona.get(prestadorKey).total += 1;
      if (!zonaMedic && mapFuera.has(prestadorKey)) mapFuera.get(prestadorKey).total += 1;
    }

    const subZona = zona.reduce((s, x) => s + x.total, 0);
    const subFuera = fuera.reduce((s, x) => s + x.total, 0);

    return { zona, fuera, subZona, subFuera };
  }, [analysisRows]);

const trasladosTabla = useMemo(() => {
    const traslados = analysisRows.filter((r) => r.tipo === "TRASLADO" || Boolean(r.socioTraslado && r.trasladoTipo));

    const init = (socio) => ({
      socio,
      UTIM: 0,
      UTIP: 0,
      CON_MEDICO: 0,
      SIN_MEDICO: 0,
      Total: 0,
    });

    const list = SOCIOS_TRASLADO.map(init);
    const map = new Map(list.map((r) => [normalizeCompareText(r.socio), r]));

    for (const r of traslados) {
      const socio = resolveOptionLabel(r.socioTraslado || "Socio directo", SOCIOS_TRASLADO);
      const socioKey = normalizeCompareText(socio);
      if (!map.has(socioKey)) continue;
      const obj = map.get(socioKey);

      const trasladoKey = normalizeTrasladoTipo(r.trasladoTipo);
      if (obj[trasladoKey] !== undefined) obj[trasladoKey] += 1;
      obj.Total += 1;
    }

    const totals = list.reduce(
      (acc, r) => {
        acc.UTIM += r.UTIM;
        acc.UTIP += r.UTIP;
        acc.CON_MEDICO += r.CON_MEDICO;
        acc.SIN_MEDICO += r.SIN_MEDICO;
        acc.Total += r.Total;
        return acc;
      },
      { UTIM: 0, UTIP: 0, CON_MEDICO: 0, SIN_MEDICO: 0, Total: 0 },
    );

    return { list, totals };
  }, [analysisRows]);

const resumenApi = useMemo(() => {
    const bases = new Set(rows.map((r) => r.base).filter(Boolean));
    const moviles = new Set(rows.map((r) => r.movil).filter(Boolean));

    return {
      total: rows.length,
      bases: bases.size,
      moviles: moviles.size,
      conConvenio: rows.filter((r) => Boolean(r.convenio)).length,
      conTipo: rows.filter((r) => Boolean(r.tipo)).length,
      conPrestador: rows.filter((r) => Boolean(r.prestador)).length,
      conHorarioOperativo: rows.filter((r) => Boolean(r.horarioOperativo)).length,
      tripulaciones: tripulacionesRows.length,
      anulados: anuladosRows.length,
    };
  }, [rows, tripulacionesRows, anuladosRows]);

  const exportAllToPDF = () => {
    const filename = `MITO_${fechaISO}_hojas.pdf`;

    const makeDoc = (orientation = "portrait") =>
      new jsPDF({ orientation, unit: "pt", format: "a4" });

    const titleStyle = (doc, title) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text(title, 40, 42);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Fecha: ${labelFecha(fechaISO)}  ·  Generado: ${hhmmss(new Date())}`, 40, 60);
    };

    const tableCommon = {
      styles: { font: "helvetica", fontSize: 8, cellPadding: 4, overflow: "linebreak" },
      headStyles: { fillColor: [207, 227, 247], textColor: 20, fontStyle: "bold" },
      margin: { left: 40, right: 40 },
    };

    const doc = makeDoc("landscape");
    titleStyle(doc, "ANULADOS");

    doc.autoTable({
      ...tableCommon,
      startY: 75,
      head: [[
        "Convenio",
        "Total",
        "Rojo",
        "Amarillo",
        "Verde",
        "Demora",
        "Horario de anulación",
        "Incidente",
        "Observaciones",
        "Localidad",
      ]],
      body: [
        ...anuladosSheetRows.map((r) => [
          r.convenio,
          r.total,
          r.rojo,
          r.amarillo,
          r.verde,
          r.demora,
          r.horarioAnulacion,
          r.incidente,
          r.observaciones,
          r.localidad,
        ]),
        [
          "Total",
          anuladosTotals.total,
          anuladosTotals.rojo,
          anuladosTotals.amarillo,
          anuladosTotals.verde,
          "",
          "",
          "",
          "",
          "",
        ],
      ],
    });

    doc.addPage("landscape");
    titleStyle(doc, "ANULADOS POR HORA");

    doc.autoTable({
      ...tableCommon,
      startY: 75,
      head: [[
        "Anulados por franja horaria",
        ...anuladosPorHora.buckets.map((b) => `${b} Hs`),
        "Total",
      ]],
      body: anuladosPorHora.rows.map((r) => [
        r.label,
        ...anuladosPorHora.buckets.map((b) => r[b] || 0),
        r.Total || 0,
      ]),
    });

    doc.addPage("portrait");
    titleStyle(doc, "PRODUCTIVIDAD (06:00 a 06:00)");

    const kv = (pairs) => pairs.map(([k, v]) => [k, String(v ?? "")]);

    doc.autoTable({
      ...tableCommon,
      startY: 75,
      head: [["Totales por color", "Valor"]],
      body: kv([
        ["Rojos", productividadResumen.totColor.ROJO],
        ["Amarillos", productividadResumen.totColor.AMARILLO],
        ["Verdes", productividadResumen.totColor.VERDE],
        ["Fucsia", productividadResumen.totColor.FUCSIA],
        ["Traslados", productividadResumen.traslados],
        ["Eventos", productividadResumen.eventos],
      ]),
      theme: "grid",
    });

    const y1 = doc.lastAutoTable.finalY + 14;

    doc.autoTable({
      ...tableCommon,
      startY: y1,
      head: [["Derivados a prestador por edad", "Valor"]],
      body: kv([
        ["Adultos", productividadResumen.derPorEdad.Adultos],
        ["Pediátricos", productividadResumen.derPorEdad["Pediátricos"]],
      ]),
      theme: "grid",
    });

    const y2 = doc.lastAutoTable.finalY + 14;

    doc.autoTable({
      ...tableCommon,
      startY: y2,
      head: [["Derivados a prestador por color", "Valor"]],
      body: kv([
        ["Rojos", productividadResumen.derPorColor.ROJO],
        ["Amarillos", productividadResumen.derPorColor.AMARILLO],
        ["Verdes", productividadResumen.derPorColor.VERDE],
        ["Fucsia", productividadResumen.derPorColor.FUCSIA],
      ]),
      theme: "grid",
    });

    const y3 = doc.lastAutoTable.finalY + 14;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(`Informe de servicios desde: ${periodDesde}`, 40, y3);
    doc.text(`Hasta: ${periodHasta}`, 40, y3 + 14);
    doc.text(`Total de servicios: ${productividadResumen.totalServicios}`, 40, y3 + 32);

    doc.autoTable({
      ...tableCommon,
      startY: y3 + 48,
      head: [["Totales base PAMI", "Rojos", "Amarillos", "Verdes", "Celeste"]],
      body: [[
        "",
        productividadResumen.pamiColor.ROJO,
        productividadResumen.pamiColor.AMARILLO,
        productividadResumen.pamiColor.VERDE,
        productividadResumen.pamiColor.CELESTE,
      ]],
    });

    doc.addPage("landscape");
    titleStyle(doc, "PRODUCTIVIDAD POR MOVIL");

    doc.autoTable({
      ...tableCommon,
      startY: 75,
      styles: { ...tableCommon.styles, fontSize: 7 },
      head: [[
        "BASE",
        "Horario",
        "Horas Operativas",
        "Realizados 08 a 20",
        "Realizados 20 a 08",
        "Total Servicios",
        "Derivaciones & Inoperantes",
        "Ingresados de 22 a 6",
        "Realizados de 22 a 6",
        "Operativo",
      ]],
      body: porMovilDisplayRows.map((r) => [
        r.base,
        r.horarioManual,
        r.horasOperativasManual,
        r.realizados8a20,
        r.realizados20a08,
        r.totalServicios,
        r.derivacionesInoperantes,
        r.ingresados22a6,
        r.realizados22a6,
        r.operativoManual,
      ]),
    });

    doc.addPage("landscape");
    titleStyle(doc, "TRASLADOS Y PRESTADORES");

    const maxLen = Math.max(prestadoresTabla.zona.length, prestadoresTabla.fuera.length);
    const prestRows = Array.from({ length: maxLen }).map((_, i) => {
      const z = prestadoresTabla.zona[i];
      const f = prestadoresTabla.fuera[i];
      return [z?.prestador || "", z?.total ?? "", f?.prestador || "", f?.total ?? ""];
    });
    prestRows.push(["Sub total:", prestadoresTabla.subZona, "Total:", prestadoresTabla.subFuera]);

    doc.autoTable({
      ...tableCommon,
      startY: 75,
      head: [["EN ZONA MEDIC (Prestador)", "Total", "FUERA DE ZONA MEDIC (Prestador)", "Total"]],
      body: prestRows,
    });

    const yPrest = doc.lastAutoTable.finalY + 18;

    doc.autoTable({
      ...tableCommon,
      startY: yPrest,
      head: [["SOCIO", "UTIM", "UTIP", "Con médico", "Sin médico", "Total"]],
      body: [
        ...trasladosTabla.list.map((r) => [
          r.socio,
          r.UTIM,
          r.UTIP,
          r.CON_MEDICO,
          r.SIN_MEDICO,
          r.Total,
        ]),
        [
          "Total",
          trasladosTabla.totals.UTIM,
          trasladosTabla.totals.UTIP,
          trasladosTabla.totals.CON_MEDICO,
          trasladosTabla.totals.SIN_MEDICO,
          trasladosTabla.totals.Total,
        ],
      ],
    });

    doc.save(filename);
  };

  const estadoBody = (row) => {
    const sev =
      row.estado === "FINALIZADO"
        ? "success"
        : row.estado === "EN_CURSO"
          ? "warning"
          : row.estado === "ANULADO"
            ? "danger"
            : "info";

    return <Tag value={String(row.estado || "").replaceAll("_", " ") || "—"} severity={sev} />;
  };

  const colorBody = (row) => <ColorBadge color={row.color} />;

  const servicioIdBody = (row) => (
    <span style={{ fontWeight: 600 }}>{row.id || "—"}</span>
  );

  const rangeBody = (row) => (
    <div style={{ display: "grid", gap: 2 }}>
      <span>{row.hora || "—"}</span>
      <span style={{ color: "#6b7280", fontSize: 12 }}>{row.horaFin || "—"}</span>
    </div>
  );

  return (
    <div className="mito-screen">
      <div className="mito-topbar">
        <div className="mito-topbar__left">
          <Button
            type="button"
            label="Volver"
            icon="pi pi-arrow-left"
            className="p-button-sm p-button-text mito-back-btn"
            onClick={() => {
              if (typeof window !== "undefined" && window.history.length > 1) {
                window.history.back();
              }
            }}
          />

          <div className="mito-title">
            <div className="mito-title__h">MITO · Servicios finalizados gráficos</div>
            <div className="mito-title__sub">
              {labelFecha(fechaISO)} · formato 24hs
              <span className="mito-dot" />
              <span className="mito-muted">Últ. actualización: {hhmmss(lastUpdate)}</span>
            </div>
          </div>
        </div>

        <div className="mito-controls">
          <div className="mito-field">
            <label>Fecha</label>
            <input
              className="mito-date"
              type="date"
              value={fechaISO}
              min={MITO_MIN_DATE}
              onChange={(e) => setFechaISO(clampFechaISO(e.target.value))}
            />
          </div>

          <div className="mito-field">
            <label>Buscar</label>
            <InputText
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="id, móvil, base, localidad..."
              className="mito-search"
            />
          </div>

          <div className="mito-actions">
            <Button
              label={autoRefresh ? "Auto ON" : "Auto OFF"}
              icon={autoRefresh ? "pi pi-refresh" : "pi pi-ban"}
              className={`p-button-sm ${autoRefresh ? "p-button-success" : "p-button-secondary"}`}
              onClick={() => setAutoRefresh((v) => !v)}
            />

            <Button
              label={loading ? "Actualizando..." : "Actualizar"}
              icon="pi pi-sync"
              className="p-button-sm p-button-outlined"
              onClick={fetchData}
              disabled={loading}
            />

            <Button
              label="Descargar PDF"
              icon="pi pi-file-pdf"
              className="p-button-sm p-button-danger"
              onClick={exportAllToPDF}
              disabled={loading}
            />

            <Button
              label="Productividad"
              icon="pi pi-chart-bar"
              className="p-button-sm"
              onClick={() => setOpenProd(true)}
            />

            <Button
              label={`Anulados (${anulados.length})`}
              icon="pi pi-times-circle"
              className="p-button-sm p-button-danger p-button-outlined"
              onClick={() => setOpenAnu(true)}
            />
          </div>
        </div>
      </div>

      <div className="mito-card" style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
            marginBottom: 14,
          }}
        >
          <div style={{ padding: 12, border: "1px solid #e5e7eb", borderRadius: 12 }}>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Endpoint</div>
            <div style={{ fontWeight: 700 }}>Finalizados gráficos</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>{lastRange.fdesde} → {lastRange.fhasta}</div>
          </div>

          <div style={{ padding: 12, border: "1px solid #e5e7eb", borderRadius: 12 }}>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Servicios cargados</div>
            <div style={{ fontWeight: 700 }}>{resumenApi.total}</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Base para listados automáticos</div>
          </div>

          <div style={{ padding: 12, border: "1px solid #e5e7eb", borderRadius: 12 }}>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Bases únicas</div>
            <div style={{ fontWeight: 700 }}>{resumenApi.bases}</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Móviles únicos: {resumenApi.moviles}</div>
          </div>

          <div style={{ padding: 12, border: "1px solid #e5e7eb", borderRadius: 12 }}>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Cobertura útil del payload</div>
            <div style={{ fontWeight: 700 }}>Convenio {resumenApi.conConvenio}</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Tipo {resumenApi.conTipo} · Prestador {resumenApi.conPrestador} · Horario {resumenApi.conHorarioOperativo}
              {` · Anulados ${resumenApi.anulados} · Tripulaciones ${resumenApi.tripulaciones}`}
            </div>
          </div>
        </div>

        {errorMsg ? (
          <div
            style={{
              background: "#fff4f4",
              border: "1px solid #fecaca",
              color: "#991b1b",
              padding: 12,
              borderRadius: 12,
              marginBottom: 12,
            }}
          >
            {errorMsg}
          </div>
        ) : null}

        <DataTable
          value={filtered}
          paginator
          rows={12}
          loading={loading}
          stripedRows
          size="small"
          responsiveLayout="scroll"
          emptyMessage="Sin registros para la fecha seleccionada."
        >
          <Column header="Servicio" body={servicioIdBody} style={{ width: 110 }} />
          <Column header="Tomado / Fin" body={rangeBody} style={{ width: 130 }} />
          <Column header="Color" body={colorBody} style={{ width: 110 }} />
          <Column field="localidad" header="Localidad" style={{ width: 180 }} />
          <Column field="base" header="Base" style={{ width: 160 }} />
          <Column field="movil" header="Móvil" style={{ width: 90 }} />
          <Column header="Estado" body={estadoBody} style={{ width: 130 }} />
          <Column field="tipo" header="Tipo" style={{ width: 120 }} />
          <Column field="convenio" header="Convenio" style={{ width: 160 }} />
          <Column field="incidente" header="Incidente / detalle" style={{ width: 220 }} />
        </DataTable>
      </div>

      <Dialog
        header={`Anulados · ${labelFecha(fechaISO)} · ${anulados.length} registros`}
        visible={openAnu}
        style={{ width: "min(1200px, 97vw)" }}
        onHide={() => setOpenAnu(false)}
        draggable={false}
      >
        <TabView>
          <TabPanel header="anulados">
            <div className="excel-sheet">
              <div className="excel-topline">
                <div className="excel-fecha">Fecha: {labelFecha(fechaISO)}</div>
              </div>

              <div className="excel-table-wrap">
                <table className="excel-table excel-anulados">
                  <thead>
                    <tr>
                      <th className="w-conv">Convenio</th>
                      <th className="w-num">Total</th>
                      <th colSpan={3} className="group">Codificación</th>
                      <th className="w-num">Demora</th>
                      <th className="w-hora">Horario de anulación</th>
                      <th className="w-inc">Incidente</th>
                      <th className="w-obs">Observaciones</th>
                      <th className="w-loc">Localidad</th>
                    </tr>
                    <tr>
                      <th />
                      <th />
                      <th className="subhead">Rojo</th>
                      <th className="subhead">Amarillo</th>
                      <th className="subhead">Verde</th>
                      <th />
                      <th />
                      <th />
                      <th />
                      <th />
                    </tr>
                  </thead>

                  <tbody>
                    {anuladosSheetRows.map((r) => (
                      <tr key={r.key}>
                        <td>{r.convenio}</td>
                        <td className="num">{r.total}</td>
                        <td className="num">{r.rojo}</td>
                        <td className="num">{r.amarillo}</td>
                        <td className="num">{r.verde}</td>
                        <td className="num">{r.demora}</td>
                        <td className="center">{r.horarioAnulacion}</td>
                        <td>{r.incidente}</td>
                        <td>{r.observaciones}</td>
                        <td>{r.localidad}</td>
                      </tr>
                    ))}

                    <tr className="excel-total-row">
                      <td className="total-label">Total</td>
                      <td className="num">{anuladosTotals.total}</td>
                      <td className="num">{anuladosTotals.rojo}</td>
                      <td className="num">{anuladosTotals.amarillo}</td>
                      <td className="num">{anuladosTotals.verde}</td>
                      <td colSpan={5} />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Anulados por hora">
            <div className="excel-sheet">
              <div className="excel-table-wrap">
                <table className="excel-table excel-horaria">
                  <thead>
                    <tr>
                      <th className="w-label">Anulados por franja Horaria</th>
                      {anuladosPorHora.buckets.map((b) => (
                        <th key={b} className="w-slot">{b} Hs</th>
                      ))}
                      <th className="w-total">Total</th>
                    </tr>
                  </thead>

                  <tbody>
                    {anuladosPorHora.rows.map((r) => (
                      <tr key={r.label} className={r.label === "Totales:" ? "excel-total-row" : ""}>
                        <td className={r.label === "Totales:" ? "total-label" : "row-label"}>{r.label}</td>
                        {anuladosPorHora.buckets.map((b) => (
                          <td key={`${r.label}-${b}`} className="num">{r[b] || 0}</td>
                        ))}
                        <td className="num">{r.Total || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabPanel>
        </TabView>
      </Dialog>

      <Dialog
        header={`Productividad · ${labelFecha(fechaISO)} · ${hhmmss(new Date())}`}
        visible={openProd}
        style={{ width: "min(1300px, 97vw)" }}
        onHide={() => setOpenProd(false)}
        draggable={false}
      >
        <TabView>
          <TabPanel header="Productividad">
            <div className="excel-sheet">
              <div className="excel-titlebar">Productividad de 06:00 a 06:00</div>

              <div className="excel-blocks3">
                <div className="excel-block">
                  <div className="excel-block__head">Totales por color:</div>
                  <div className="excel-kv">
                    <div>Rojos:</div><div className="num">{productividadResumen.totColor.ROJO}</div>
                    <div>Amarillos:</div><div className="num">{productividadResumen.totColor.AMARILLO}</div>
                    <div>Verde:</div><div className="num">{productividadResumen.totColor.VERDE}</div>
                    <div>Fucsia:</div><div className="num">{productividadResumen.totColor.FUCSIA}</div>
                    <div>Traslados:</div><div className="num">{productividadResumen.traslados}</div>
                    <div>Eventos:</div><div className="num">{productividadResumen.eventos}</div>
                  </div>
                </div>

                <div className="excel-block">
                  <div className="excel-block__head">Derivados a prestador por edad:</div>
                  <div className="excel-kv">
                    <div>Adultos:</div><div className="num">{productividadResumen.derPorEdad.Adultos}</div>
                    <div>Pediátricos:</div><div className="num">{productividadResumen.derPorEdad["Pediátricos"]}</div>
                  </div>
                </div>

                <div className="excel-block">
                  <div className="excel-block__head">Derivados a prestador por color:</div>
                  <div className="excel-kv">
                    <div>Rojos:</div><div className="num">{productividadResumen.derPorColor.ROJO}</div>
                    <div>Amarillos:</div><div className="num">{productividadResumen.derPorColor.AMARILLO}</div>
                    <div>Verde:</div><div className="num">{productividadResumen.derPorColor.VERDE}</div>
                    <div>Fucsia:</div><div className="num">{productividadResumen.derPorColor.FUCSIA}</div>
                  </div>
                </div>
              </div>

              <div className="excel-infobar">
                <div className="excel-infobar__label">Informe de servicios desde:</div>
                <div className="excel-infobar__value">{periodDesde}</div>
                <div className="excel-infobar__label">Hasta:</div>
                <div className="excel-infobar__value">{periodHasta}</div>
              </div>

              <div className="excel-totalserv">
                <div className="excel-totalserv__label">Total de Servicios:</div>
                <div className="excel-totalserv__value num">{productividadResumen.totalServicios}</div>
              </div>

              <div className="excel-pami">
                <div className="excel-pami__head">Totales base PAMI</div>
                <div className="excel-pami__cols">
                  <div className="excel-pami__col">Rojos</div>
                  <div className="excel-pami__col">Amarillos</div>
                  <div className="excel-pami__col">Verdes</div>
                  <div className="excel-pami__col">Celeste</div>
                </div>
                <div className="excel-pami__vals">
                  <div className="num">{productividadResumen.pamiColor.ROJO}</div>
                  <div className="num">{productividadResumen.pamiColor.AMARILLO}</div>
                  <div className="num">{productividadResumen.pamiColor.VERDE}</div>
                  <div className="num">{productividadResumen.pamiColor.CELESTE}</div>
                </div>
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Por movil">
            <div className="excel-sheet">
              <div className="excel-wide-title">Productividad según el rango horario de cada movil</div>

              <div className="excel-table-wrap">
                <table className="excel-table excel-movil">
                  <thead>
                    <tr>
                      <th className="w-base">BASE</th>
                      <th className="w-hor">Horario</th>
                      <th className="w-hs">Horas Operativas</th>
                      <th className="w-num">Realizados 08 a 20</th>
                      <th className="w-num">Realizados 20 a 08</th>
                      <th className="w-num">Total Servicios</th>
                      <th className="w-deriv">Derivaciones &amp; Inoperantes</th>
                      <th className="w-num">Ingresados de 22 a 6</th>
                      <th className="w-num">Realizados de 22 a 6</th>
                      <th className="w-op">Operativo</th>
                    </tr>
                  </thead>

                  <tbody>
                    {porMovilDisplayRows.map((r, idx) => (
                      <tr key={`${r.base}-${r.movil || idx}`}>
                        <td className="cell-blue">{r.base}</td>
                        <td className="cell-blue center">
                          <input
                            type="text"
                            value={r.horarioManual}
                            onChange={(e) => updatePorMovilManual(r, "horario", e.target.value)}
                            className="excel-inline-input"
                            placeholder=""
                            autoComplete="off"
                          />
                        </td>
                        <td className="cell-gray center">
                          <input
                            type="text"
                            value={r.horasOperativasManual}
                            onChange={(e) => updatePorMovilManual(r, "horasOperativas", e.target.value)}
                            className="excel-inline-input excel-inline-input--sm"
                            placeholder=""
                            autoComplete="off"
                          />
                        </td>
                        <td className="num">{r.realizados8a20}</td>
                        <td className="num">{r.realizados20a08}</td>
                        <td className="num cell-yellow">{r.totalServicios}</td>
                        <td className="num">{r.derivacionesInoperantes}</td>
                        <td className="num">{r.ingresados22a6}</td>
                        <td className="num">{r.realizados22a6}</td>
                        <td className="cell-gray center">
                          <input
                            type="text"
                            value={r.operativoManual}
                            onChange={(e) => updatePorMovilManual(r, "operativo", e.target.value)}
                            className="excel-inline-input excel-inline-input--sm"
                            placeholder=""
                            autoComplete="off"
                          />
                        </td>
                      </tr>
                    ))}
                    <tr className="excel-total-row">
                      <td colSpan={10} />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Traslados y prestadores">
            <div className="excel-sheet">
              <div className="excel-table-dual">
                <div className="excel-table-wrap">
                  <table className="excel-table excel-prest">
                    <thead>
                      <tr>
                        <th colSpan={4} className="group gray-head">REALIZADOS POR PRESTADOR</th>
                      </tr>
                      <tr>
                        <th colSpan={2} className="group">EN ZONA MEDIC</th>
                        <th colSpan={2} className="group">FUERA DE ZONA MEDIC</th>
                      </tr>
                      <tr>
                        <th className="subhead">PRESTADOR</th>
                        <th className="subhead w-num">Total</th>
                        <th className="subhead">PRESTADOR</th>
                        <th className="subhead w-num">Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      {Array.from({ length: Math.max(prestadoresTabla.zona.length, prestadoresTabla.fuera.length) }).map((_, i) => {
                        const z = prestadoresTabla.zona[i];
                        const f = prestadoresTabla.fuera[i];
                        return (
                          <tr key={i}>
                            <td className="cell-blue">{z?.prestador || ""}</td>
                            <td className="num">{z?.total ?? ""}</td>
                            <td className="cell-blue">{f?.prestador || ""}</td>
                            <td className="num">{f?.total ?? ""}</td>
                          </tr>
                        );
                      })}

                      <tr className="excel-total-row">
                        <td className="total-label">Sub total:</td>
                        <td className="num">{prestadoresTabla.subZona}</td>
                        <td className="total-label">Total</td>
                        <td className="num">{prestadoresTabla.subFuera}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="excel-table-wrap">
                  <table className="excel-table excel-trasl">
                    <thead>
                      <tr>
                        <th colSpan={6} className="group gray-head">TRASLADOS</th>
                      </tr>
                      <tr>
                        <th className="subhead w-socio">SOCIO</th>
                        <th className="subhead w-num">UTIM</th>
                        <th className="subhead w-num">UTIP</th>
                        <th className="subhead w-num">Con medico</th>
                        <th className="subhead w-num">Sin medico</th>
                        <th className="subhead w-num">Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      {trasladosTabla.list.map((r, idx) => (
                        <tr key={`${r.socio}-${idx}`}>
                          <td className="cell-blue">{r.socio}</td>
                          <td className="num cell-gray">{r.UTIM}</td>
                          <td className="num cell-gray">{r.UTIP}</td>
                          <td className="num cell-gray">{r.CON_MEDICO}</td>
                          <td className="num cell-gray">{r.SIN_MEDICO}</td>
                          <td className="num cell-blue2">{r.Total}</td>
                        </tr>
                      ))}

                      <tr className="excel-total-row">
                        <td className="total-label">Total</td>
                        <td className="num">{trasladosTabla.totals.UTIM}</td>
                        <td className="num">{trasladosTabla.totals.UTIP}</td>
                        <td className="num">{trasladosTabla.totals.CON_MEDICO}</td>
                        <td className="num">{trasladosTabla.totals.SIN_MEDICO}</td>
                        <td className="num">{trasladosTabla.totals.Total}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="excel-note">Total 24 Convenios</div>
                </div>
              </div>
            </div>
          </TabPanel>
        </TabView>
      </Dialog>
    </div>
  );
}
